import React from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTrigger,
  Flex,
  LoadingOrbit,
  Text,
  TextField,
  useToast,
} from '@chatsdet/ui';
import { Warning } from '@phosphor-icons/react';
import {
  useDeleteOrganizationMutation,
  useOrgMembersQuery,
  useToggleOrgStatusMutation,
  type OrganizationWithStats,
} from './useSuperadminData';

type OrganizationStatus = OrganizationWithStats['status'];

export function nextOrganizationStatus(status: OrganizationStatus): OrganizationStatus {
  return status === 'active' ? 'inactive' : 'active';
}

export function canConfirmOrganizationStatusChange({
  status,
  orgName,
  confirmText,
}: {
  status: OrganizationStatus;
  orgName: string;
  confirmText: string;
}): boolean {
  return status === 'active' ? confirmText === orgName : true;
}

export function ToggleOrgStatusDialog({
  org,
  onClose,
}: {
  org: OrganizationWithStats | null;
  onClose: () => void;
}) {
  const [confirmText, setConfirmText] = React.useState('');
  const [reason, setReason] = React.useState('');
  const mutation = useToggleOrgStatusMutation();
  const { toast } = useToast();

  React.useEffect(() => {
    if (org) {
      setConfirmText('');
      setReason('');
    }
  }, [org?.id]);

  if (!org) {
    return (
      <DialogTrigger isOpen={false} onOpenChange={() => {}}>
        <span />
        <Dialog title="Organization status">
          <DialogContent>
            <Box />
          </DialogContent>
        </Dialog>
      </DialogTrigger>
    );
  }

  const disabling = org.status === 'active';
  const nextStatus = nextOrganizationStatus(org.status);
  const canConfirm = canConfirmOrganizationStatusChange({
    status: org.status,
    orgName: org.name,
    confirmText,
  });

  const handleSubmit = () => {
    if (!canConfirm) return;
    mutation.mutate(
      { orgId: org.id, status: nextStatus, reason: reason || null },
      {
        onSuccess: () => {
          toast.success(disabling ? `Disabled ${org.name}` : `Re-enabled ${org.name}`);
          onClose();
          mutation.reset();
        },
        onError: (err) => toast.error(err.message),
      }
    );
  };

  return (
    <DialogTrigger
      isOpen={!!org}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onClose();
          mutation.reset();
        }
      }}
      size="lg"
    >
      <span />
      <Dialog title={disabling ? 'Disable organization' : 'Re-enable organization'}>
        <DialogContent>
          <Box className="space-y-4">
            {disabling ? (
              <>
                <Flex align="start" gap="sm">
                  <Warning size={20} className="text-destructive mt-0.5" />
                  <Text>
                    Disabling{' '}
                    <Text weight="bold" as="span">
                      {org.name}
                    </Text>{' '}
                    will immediately block every user in this organization from logging in or using
                    the API, and will end all active sessions. Data is preserved and can be restored
                    by re-enabling.
                  </Text>
                </Flex>
                <Box>
                  <Text size="sm" className="text-muted-foreground mb-1 block">
                    Type{' '}
                    <Text weight="bold" as="span">
                      {org.name}
                    </Text>{' '}
                    to confirm
                  </Text>
                  <TextField value={confirmText} onChange={setConfirmText} placeholder={org.name} />
                </Box>
              </>
            ) : (
              <Text>
                Re-enable{' '}
                <Text weight="bold" as="span">
                  {org.name}
                </Text>
                ? Users will be able to log in again. Existing data is untouched.
              </Text>
            )}

            <Box>
              <Text size="sm" className="text-muted-foreground mb-1 block">
                Reason (optional)
              </Text>
              <TextField value={reason} onChange={setReason} placeholder="Stored in audit log" />
            </Box>
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={onClose} isDisabled={mutation.isPending}>
            Cancel
          </Button>
          <Button
            variant={disabling ? 'destructive' : 'primary'}
            onPress={handleSubmit}
            isDisabled={!canConfirm || mutation.isPending}
          >
            {mutation.isPending
              ? 'Saving...'
              : disabling
                ? 'Disable organization'
                : 'Re-enable organization'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}

export function DeleteOrganizationDialog({
  org,
  onClose,
}: {
  org: OrganizationWithStats | null;
  onClose: () => void;
}) {
  const [confirmText, setConfirmText] = React.useState('');
  const deleteMutation = useDeleteOrganizationMutation();
  const membersQuery = useOrgMembersQuery(org?.id ?? null);

  React.useEffect(() => {
    if (org) setConfirmText('');
  }, [org?.id]);

  const handleDelete = () => {
    if (!org || confirmText !== org.name) return;
    deleteMutation.mutate(org.id, {
      onSuccess: () => {
        onClose();
        deleteMutation.reset();
      },
    });
  };

  return (
    <DialogTrigger
      isOpen={!!org}
      onOpenChange={(open: boolean) => {
        if (!open) {
          onClose();
          deleteMutation.reset();
        }
      }}
      size="lg"
    >
      <span />
      <Dialog title="Delete Organization">
        <DialogContent>
          <Box className="space-y-4">
            <Text>
              Are you sure you want to delete{' '}
              <Text weight="bold" as="span">
                {org?.name}
              </Text>
              ? This action cannot be undone.
            </Text>

            {membersQuery.isLoading ? (
              <Flex align="center" justify="center" className="py-4">
                <LoadingOrbit size="sm" />
              </Flex>
            ) : membersQuery.data?.members && membersQuery.data.members.length > 0 ? (
              <Box>
                <Text size="sm" weight="medium" className="mb-2">
                  Members ({membersQuery.data.members.length})
                </Text>
                <Box className="max-h-48 overflow-auto border-y border-border divide-y divide-border">
                  {membersQuery.data.members.map((m) => (
                    <Flex key={m.userId} align="center" justify="between" className="px-3 py-2">
                      <Box>
                        <Text size="sm" weight="medium">
                          {m.displayName ?? 'Unknown'}
                        </Text>
                        <Text size="xs" className="text-muted-foreground">
                          {m.primaryEmail}
                        </Text>
                      </Box>
                      <Text size="xs" className="text-muted-foreground capitalize">
                        {m.role}
                      </Text>
                    </Flex>
                  ))}
                </Box>
              </Box>
            ) : null}

            {deleteMutation.isError && (
              <Flex align="center" gap="sm" className="p-3 rounded-md bg-destructive/10">
                <Warning size={20} className="text-destructive" />
                <Text size="sm" className="text-destructive">
                  {deleteMutation.error?.message ?? 'Failed to delete organization'}
                </Text>
              </Flex>
            )}

            <TextField
              label={`Type "${org?.name}" to confirm`}
              value={confirmText}
              onChange={setConfirmText}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={onClose}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onPress={handleDelete}
            isDisabled={confirmText !== org?.name || deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Deleting...' : 'Delete Organization'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
